/**
 * Product showcase carousel (Slick): all showcase videos play muted in a loop at once.
 * Image-only slides still advance on the configured interval.
 */
(function () {
  'use strict';

  var PREV_ARROW_HTML =
    '<button type="button" class="slick-prev product-showcase-carousel__arrow">' +
    '<span class="product-showcase-carousel__arrow-icon product-showcase-carousel__arrow-icon--prev" aria-hidden="true"></span>' +
    '</button>';

  var NEXT_ARROW_HTML =
    '<button type="button" class="slick-next product-showcase-carousel__arrow">' +
    '<span class="product-showcase-carousel__arrow-icon" aria-hidden="true"></span>' +
    '</button>';

  /**
   * @param {Element | null | undefined} node
   * @returns {HTMLElement | null}
   */
  function resolveSlideEl(node) {
    if (!(node instanceof HTMLElement)) return null;
    if (node.classList.contains('js-product-showcase-slide')) return node;
    var inner = node.querySelector('.js-product-showcase-slide');
    return inner instanceof HTMLElement ? inner : null;
  }

  /**
   * @param {HTMLVideoElement} video
   */
  function configureShowcaseVideo(video) {
    if (video.hasAttribute('controls')) return;
    if (video.dataset.showcaseConfigured === 'true') return;

    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.loop = true;
    video.setAttribute('loop', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.autoplay = true;
    video.controls = false;
    video.removeAttribute('controls');
    video.setAttribute('controlsList', 'nodownload noplaybackrate noremoteplayback');
    video.setAttribute('disablePictureInPicture', '');
    video.preload = 'metadata';

    video.addEventListener(
      'ended',
      function () {
        if (!video.loop) return;
        video.currentTime = 0;
        video.play().catch(function () {});
      },
      { passive: true }
    );

    video.dataset.showcaseConfigured = 'true';
  }

  /**
   * @param {HTMLVideoElement} video
   * @returns {Promise<void>}
   */
  function playVideo(video) {
    configureShowcaseVideo(video);

    if (!video.paused && !video.ended) {
      return Promise.resolve();
    }

    return video.play().catch(function () {
      video.muted = true;
      video.setAttribute('muted', '');
      return video.play().catch(function () {});
    });
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLVideoElement[]}
   */
  function getShowcaseVideos(root) {
    /** @type {HTMLVideoElement[]} */
    var videos = [];

    root.querySelectorAll('.product-showcase-carousel__video').forEach(function (node) {
      if (!(node instanceof HTMLVideoElement) || node.hasAttribute('controls')) return;

      var slickSlide = node.closest('.slick-slide');
      if (slickSlide && slickSlide.classList.contains('slick-cloned')) return;

      videos.push(node);
    });

    return videos;
  }

  /**
   * In infinite mode Slick renders cloned slides. We want the visible clones to
   * autoplay too, otherwise the carousel looks \"stuck\" when a clone is active.
   *
   * @param {HTMLElement} root
   * @returns {HTMLVideoElement[]}
   */
  function getActiveCloneVideos(root) {
    /** @type {HTMLVideoElement[]} */
    var videos = [];

    root
      .querySelectorAll('.slick-slide.slick-active.slick-cloned .product-showcase-carousel__video')
      .forEach(function (node) {
        if (node instanceof HTMLVideoElement && !node.hasAttribute('controls')) {
          videos.push(node);
        }
      });

    return videos;
  }

  /**
   * @param {HTMLVideoElement} video
   */
  function playVideoWhenReady(video) {
    configureShowcaseVideo(video);

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      playVideo(video);
      return;
    }

    video.addEventListener(
      'canplay',
      function () {
        playVideo(video);
      },
      { once: true }
    );
  }

  /**
   * @param {HTMLElement} root
   */
  function playAllShowcaseVideos(root) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    getShowcaseVideos(root).forEach(playVideoWhenReady);
    getActiveCloneVideos(root).forEach(playVideoWhenReady);
  }

  /**
   * @param {any} slick
   * @returns {HTMLElement[]}
   */
  function getVisibleSlideEls(slick) {
    /** @type {HTMLElement[]} */
    var results = [];

    if (slick && slick.$slider) {
      slick.$slider.find('.slick-slide.slick-active').each(function () {
        var slideEl = resolveSlideEl(this);
        if (slideEl) results.push(slideEl);
      });
    }

    if (results.length === 0 && slick && slick.$slides && slick.$slides.length) {
      var fallback = resolveSlideEl(slick.$slides.get(slick.currentSlide));
      if (fallback) results.push(fallback);
    }

    return results;
  }

  /**
   * @param {HTMLElement} root
   */
  function markCarouselReady(root) {
    root.classList.add('product-showcase-carousel--ready');
    playAllShowcaseVideos(root);
    requestAnimationFrame(function () {
      playAllShowcaseVideos(root);
    });
    window.setTimeout(function () {
      playAllShowcaseVideos(root);
    }, 300);
  }

  /**
   * @param {HTMLElement} root
   */
  function initSection(root) {
    getShowcaseVideos(root).forEach(configureShowcaseVideo);
    getActiveCloneVideos(root).forEach(configureShowcaseVideo);

    var $ = window.jQuery;
    if (!$ || typeof $.fn.slick !== 'function') {
      markCarouselReady(root);
      return;
    }

    var $slider = $(root).find('.js-product-showcase-slider');
    if (!$slider.length || $slider.hasClass('slick-initialized')) {
      markCarouselReady(root);
      return;
    }

    var $mediaRow = $(root).find('.product-showcase-carousel__media-row');
    var $dotsTarget = $(root).find('.product-showcase-carousel__dots-target');

    var slideIntervalMs = parseInt(String(root.dataset.slideInterval || '5000'), 10);
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /** @type {ReturnType<typeof setTimeout> | null} */
    var advanceTimer = null;

    function clearAdvanceTimer() {
      if (advanceTimer) {
        clearTimeout(advanceTimer);
        advanceTimer = null;
      }
    }

    function scheduleAdvance() {
      clearAdvanceTimer();
      if (reducedMotion) return;
      advanceTimer = window.setTimeout(function () {
        $slider.slick('slickNext');
      }, slideIntervalMs);
    }

    /**
     * @param {any} slick
     */
    function activateSlide(slick) {
      clearAdvanceTimer();
      playAllShowcaseVideos(root);

      var visibleSlides = getVisibleSlideEls(slick);
      var hasImageOnlySlide = visibleSlides.some(function (slide) {
        return slide.dataset.hasVideo !== 'true';
      });

      if (hasImageOnlySlide) {
        scheduleAdvance();
      }
    }

    var slidesToShow = parseInt(String(root.dataset.slidesToShow || '3'), 10);
    var slidesToShowTablet = parseInt(String(root.dataset.slidesToShowTablet || '2'), 10);
    var slidesToShowMobile = parseInt(String(root.dataset.slidesToShowMobile || '1'), 10);
    var initialSlide = parseInt(String(root.dataset.initialSlide || '0'), 10);
    var speed = parseInt(String(root.dataset.speed || '300'), 10);

    var slickOptions = {
      infinite: root.dataset.infinite !== 'false',
      slidesToShow: slidesToShow,
      slidesToScroll: 1,
      initialSlide: Number.isFinite(initialSlide) ? initialSlide : 0,
      centerMode: root.dataset.centerMode === 'true',
      centerPadding: root.dataset.centerPadding || '60px',
      speed: Number.isFinite(speed) ? speed : 300,
      arrows: root.dataset.arrows !== 'false',
      dots: root.dataset.dots === 'true',
      accessibility: true,
      swipe: true,
      prevArrow: PREV_ARROW_HTML,
      nextArrow: NEXT_ARROW_HTML,
      appendArrows: $mediaRow.length ? $mediaRow : $slider,
      appendDots: $dotsTarget.length ? $dotsTarget : $slider,
      responsive: [
        {
          breakpoint: 990,
          settings: {
            slidesToShow: slidesToShowTablet,
            slidesToScroll: 1,
            centerPadding: '40px',
          },
        },
        {
          breakpoint: 749,
          settings: {
            slidesToShow: slidesToShowMobile,
            slidesToScroll: 1,
            centerPadding: root.dataset.centerPaddingMobile || '24px',
            centerMode: true,
          },
        },
      ],
    };

    function bindAria() {
      requestAnimationFrame(function () {
        var prevBtn = root.querySelector('.product-showcase-carousel__media-row .slick-prev');
        var nextBtn = root.querySelector('.product-showcase-carousel__media-row .slick-next');
        if (prevBtn && root.dataset.labelPrev) {
          prevBtn.setAttribute('aria-label', root.dataset.labelPrev);
        }
        if (nextBtn && root.dataset.labelNext) {
          nextBtn.setAttribute('aria-label', root.dataset.labelNext);
        }
      });
    }

    $slider.on('init', function (_event, slick) {
      markCarouselReady(root);
      bindAria();
      activateSlide(slick);
    });

    $slider.on('afterChange', function (_event, slick) {
      activateSlide(slick);
    });

    try {
      $slider.slick(slickOptions);
    } catch {
      markCarouselReady(root);
    }
  }

  function initAll() {
    document.querySelectorAll('[data-product-showcase-carousel]').forEach(function (root) {
      if (root instanceof HTMLElement) initSection(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (!target || !(target instanceof HTMLElement)) return;
    var root = target.querySelector('[data-product-showcase-carousel]');
    if (root instanceof HTMLElement) initSection(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var target = event.target;
    if (!target || !(target instanceof HTMLElement)) return;
    var root = target.querySelector('[data-product-showcase-carousel]');
    if (!(root instanceof HTMLElement) || !window.jQuery) return;

    var $slider = window.jQuery(root).find('.js-product-showcase-slider');
    if ($slider.length && $slider.hasClass('slick-initialized')) {
      $slider.slick('unslick');
    }
    root.classList.remove('product-showcase-carousel--ready');
  });
})();
